import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { ClientFilters } from '@/components/clients/ClientFilters';
import { ClientTable } from '@/components/clients/ClientTable';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Plus, Download } from 'lucide-react';
import { mockClients } from '@/lib/mock-entities';
import type { ClientStatus, ClientSource } from '@/types/entities';

export default function ClientsList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ClientStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<ClientSource | 'all'>('all');

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return mockClients;
    },
  });

  const filteredClients = useMemo(() => {
    if (!clients) return [];

    return clients.filter((client) => {
      const matchesSearch =
        searchQuery === '' ||
        client.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
      const matchesSource = sourceFilter === 'all' || client.source === sourceFilter;

      return matchesSearch && matchesStatus && matchesSource;
    });
  }, [clients, searchQuery, statusFilter, sourceFilter]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSourceFilter('all');
  };

  const handleExport = () => {
    console.log('Export clients to CSV');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Clients" />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="Clients">
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => navigate('/clients/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </Header>

      <main className="flex-1 overflow-auto p-6">
        <div className="mb-6">
          <ClientFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            sourceFilter={sourceFilter}
            onSourceChange={setSourceFilter}
            onReset={handleResetFilters}
          />
        </div>

        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredClients.length} of {clients?.length || 0} clients
          </p>
        </div>

        <ClientTable clients={filteredClients} />
      </main>
    </div>
  );
}
